import { Fzf, FzfResultItem } from "fzf";
import * as React from "react";
import { normalizeForSearch } from "../../../../shared/utility/search-normalize";
import { ConfigPaneKey } from "./config-categories";

/**
 * The searchable picture of an open configuration.
 *
 * There is no central schema of the ~300 settings a configuration holds: every label is
 * written in the leaf that draws it, and the only externalised text is the description and
 * the tooltip the backend hands over by key. So the index is not declared — it is COLLECTED.
 * Every `ConfigSection` and every `ConfigField` registers itself as it mounts, and since all
 * five panes and all fifty-one categories stay mounted (see `config-pane-shell.tsx`), the
 * collection is complete the moment the editor is open. A category the user cannot have —
 * a plan they are not on, a platform the project does not build — is never mounted, so it
 * is never in the index either; the search inherits the panes' gating for free.
 *
 * The one thing mounting cannot see is the inside of a settings dialog, which exists only
 * while it is open. Those hand their field names over as `terms`, a list kept next to the
 * dialog that owns them (`*_SEARCH_TERMS`), and a hit on one of those is what makes a result
 * open the dialog rather than just land on the row.
 *
 * This module is the pure half: the entry model, the store the parts register into, and the
 * ranking. It knows nothing about React rendering beyond `React.Children`, which
 * `reactNodeText` needs to flatten a label that was handed over as markup.
 */

export type ConfigSearchEntryKind = "section" | "field";

export interface IConfigSearchEntry {
  /** Stable for the life of the mounted part; also the DOM anchor `data-config-search-id`. */
  id: string;
  kind: ConfigSearchEntryKind;
  pane: ConfigPaneKey;
  /** Undefined for a section of the pane's own column — it belongs to the platform, not to a category. */
  categoryId?: string;
  /** The category's label, or the rail section's title. What the breadcrumb ends on. */
  sectionTitle: string;
  /** The field's label; a section's is its title. */
  label: string;
  description?: string;
  /** Key into `sd.configLocalizations` — resolved when the haystack is built, never stored here. */
  tooltipKey?: string;
  /** Field names inside the dialog this row opens. */
  terms: string[];
  /**
   * Whether a hit on `terms` can actually open anything.
   *
   * False for a row that lists what its dialog holds but has no picker to open it with — the
   * REST API rows drive their own control. Without this the result would carry a badge and a
   * request the row has nothing to answer with.
   */
  termsOpenDialog: boolean;
}

export type IConfigSearchEntryDraft = Omit<IConfigSearchEntry, "id">;

export type ConfigSearchListener = () => void;

/** What a mounted part needs from the index, so a tree without one can be handed a no-op. */
export interface IConfigSearchRegistry {
  register: (entry: IConfigSearchEntry) => void;
  unregister: (id: string) => void;
  entries: () => IConfigSearchEntry[];
  subscribe: (listener: ConfigSearchListener) => () => void;
}

/**
 * The registry the parts write into. Insertion order is mount order, which is pane order
 * then category order then row order — the order a reader would meet the settings in — and
 * that is the tie-break the ranking falls back on.
 */
export class ConfigSearchIndex implements IConfigSearchRegistry {
  private readonly entriesById: Map<string, IConfigSearchEntry> = new Map<string, IConfigSearchEntry>();

  private readonly listeners: Set<ConfigSearchListener> = new Set<ConfigSearchListener>();

  register(entry: IConfigSearchEntry): void {
    this.entriesById.set(entry.id, entry);
    this.notify();
  }

  unregister(id: string): void {
    if (!this.entriesById.delete(id)) return;
    this.notify();
  }

  entries(): IConfigSearchEntry[] {
    return [...this.entriesById.values()];
  }

  subscribe(listener: ConfigSearchListener): () => void {
    this.listeners.add(listener);
    return (): void => {
      this.listeners.delete(listener);
    };
  }

  private notify(): void {
    this.listeners.forEach((listener: ConfigSearchListener): void => listener());
  }
}

/**
 * The index for a tree that has no search box above it — the pane and shell tests render
 * several. It has to be inert rather than merely unread: a real instance at module scope
 * would collect every field of every test file that ever mounts one, in one process.
 */
export const NOOP_CONFIG_SEARCH_REGISTRY: IConfigSearchRegistry = {
  register: (): void => {},
  unregister: (): void => {},
  entries: (): IConfigSearchEntry[] => [],
  subscribe: (): (() => void) => (): void => {}
};

let nextId: number = 0;

/** Ids for the mounted parts. A counter rather than `useId`: the designer is on React 17. */
export const nextConfigSearchId = (): string => {
  nextId += 1;
  return `config-search-${nextId}`;
};

const MAX_TEXT_DEPTH: number = 4;

/**
 * The plain text of a label or description written as markup.
 *
 * Labels are mostly strings, but a description is a fragment of text and a "Learn more"
 * link, and the odd label carries a badge. Reading the strings out of the tree is enough for
 * search; the link text coming along is harmless. Bounded like `findFormItemPath` is — a
 * label is never deep, and an unbounded walk over an unknown element is not worth the risk.
 */
export const reactNodeText = (node: React.ReactNode, depth: number = 0): string => {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string") return node.replace(/\s+/g, " ").trim();
  if (typeof node === "number") return String(node);
  if (depth > MAX_TEXT_DEPTH) return "";

  const parts: string[] = [];
  React.Children.forEach(node, (child: React.ReactNode): void => {
    const text: string = React.isValidElement(child)
      ? reactNodeText(child.props.children, depth + 1)
      : reactNodeText(child, depth + 1);
    if (text.length > 0) parts.push(text);
  });
  return parts.join(" ").replace(/\s+/g, " ").trim();
};

export interface IConfigSearchRange {
  /** Inclusive start, exclusive end — indices into the ORIGINAL text. */
  start: number;
  end: number;
}

/**
 * `normalizeForSearch` plus the dotless ı.
 *
 * The shared helper handles every letter that carries a combining mark — "İ" lowercases to
 * `i` + U+0307 and the NFD pass drops the dot, so "İZİN" and "izin" meet. U+0131 is the one
 * Turkish letter it cannot reach: `toLowerCase` leaves it alone, NFD does not decompose it,
 * and it has no mark to strip, so "tımeout" typed on a Turkish keyboard would match nothing.
 * Folding it here rather than in the shared helper keeps that helper's contract — and its
 * four other consumers — exactly as they are.
 */
const normalizeConfigSearchChar = (character: string): string => normalizeForSearch(character).replace(/ı/g, "i");

interface INormalizedText {
  original: string;
  normalized: string;
  /** For each character of `normalized`, the index of the original character it came from. */
  originIndex: number[];
}

/**
 * The normalized form of a text, character by character, so a match found in it can be
 * pointed at in the original. NFD is what makes the two differ: "İ" becomes two code points
 * before the mark is stripped, and a range measured on the normalized string would land a
 * character or two off on the string the reader is looking at.
 */
export const normalizeWithIndexMap = (text: string): INormalizedText => {
  let normalized: string = "";
  const originIndex: number[] = [];
  for (let i = 0; i < text.length; i += 1) {
    const piece: string = normalizeConfigSearchChar(text[i]);
    for (let j = 0; j < piece.length; j += 1) {
      normalized += piece[j];
      originIndex.push(i);
    }
  }
  return { original: text, normalized, originIndex };
};

/** The normalized form of a query. Same folding, no index map — nothing points back at it. */
export const normalizeConfigSearchText = (text: string): string => normalizeWithIndexMap(text).normalized;

const toOriginalRange = (text: INormalizedText, start: number, end: number): IConfigSearchRange => ({
  start: text.originIndex[start],
  end: text.originIndex[end - 1] + 1
});

/** Coalesce matched character positions into runs. */
export const positionsToRanges = (positions: Iterable<number>): IConfigSearchRange[] => {
  const sorted: number[] = [...positions].sort((a: number, b: number): number => a - b);
  const ranges: IConfigSearchRange[] = [];
  sorted.forEach((position: number): void => {
    const last: IConfigSearchRange | undefined = ranges[ranges.length - 1];
    if (last && last.end === position) {
      last.end = position + 1;
      return;
    }
    ranges.push({ start: position, end: position + 1 });
  });
  return ranges;
};

/** Merge overlapping or touching ranges so a token matched twice does not double-mark. */
export const mergeRanges = (ranges: IConfigSearchRange[]): IConfigSearchRange[] => {
  const sorted: IConfigSearchRange[] = [...ranges].sort(
    (a: IConfigSearchRange, b: IConfigSearchRange): number => a.start - b.start
  );
  const merged: IConfigSearchRange[] = [];
  sorted.forEach((range: IConfigSearchRange): void => {
    const last: IConfigSearchRange | undefined = merged[merged.length - 1];
    if (last && range.start <= last.end) {
      last.end = Math.max(last.end, range.end);
      return;
    }
    merged.push({ ...range });
  });
  return merged;
};

export type ConfigSearchSnippetSource = "description" | "tooltip" | "terms";

export interface IConfigSearchSnippet {
  text: string;
  ranges: IConfigSearchRange[];
  source: ConfigSearchSnippetSource;
}

export interface IConfigSearchResult {
  entry: IConfigSearchEntry;
  score: number;
  labelRanges: IConfigSearchRange[];
  /** Ranges on `entry.sectionTitle`, for the breadcrumb. */
  sectionRanges: IConfigSearchRange[];
  snippet?: IConfigSearchSnippet;
  /** The match came from inside the dialog this row opens, so opening it is the destination. */
  openDialog: boolean;
}

export interface IConfigSearchPane {
  key: ConfigPaneKey;
  label: string;
}

export interface IConfigSearchOptions {
  /** The panes the editor is showing, in tab order. Entries on a pane not listed here are dropped. */
  panes: IConfigSearchPane[];
  /**
   * Panes whose platform support is off. Their fields are still mounted and still register —
   * the support toggle hides the pane's body with CSS — so dropping them here is the only
   * thing standing between the reader and a result they cannot navigate to.
   */
  hiddenPanes: ConfigPaneKey[];
  tooltipText: (key: string) => string | undefined;
  /** Results past this many are cut. The list is a picker, not a report. */
  limit?: number;
}

const DEFAULT_LIMIT: number = 40;

/**
 * How much a hit is worth, by where it landed. A category's name is the strongest signal —
 * someone typing "session" most likely wants the Sessions category — then the row's own
 * label, then what a dialog holds, then the prose around it. Fuzzy is last and only ever a
 * fallback: it runs when nothing contained the query at all.
 */
const SCORE = {
  sectionExact: 100,
  sectionPrefix: 80,
  sectionContains: 60,
  labelExact: 90,
  labelPrefix: 70,
  labelContains: 50,
  terms: 45,
  prose: 30,
  fuzzy: 10
} as const;

/**
 * One entry with every text it can be matched on already normalized.
 *
 * Opaque on purpose: the caller's job is to build these once per index change and hand the
 * same array to every query. Normalizing ~400 entries character by character is far too much
 * work to repeat on a keystroke, and `searchConfigEntries` used to do exactly that.
 */
export interface IConfigSearchHaystack {
  entry: IConfigSearchEntry;
  section: INormalizedText;
  label: INormalizedText;
  description?: INormalizedText;
  tooltip?: INormalizedText;
  terms: INormalizedText[];
}

/**
 * Normalize the index for querying. Rebuild it when the index changes or when the
 * localizations arrive — `tooltipText` reads those, and a haystack built before they landed
 * holds no tooltip text at all.
 */
export const buildConfigSearchHaystacks = (
  entries: IConfigSearchEntry[],
  options: IConfigSearchOptions
): IConfigSearchHaystack[] => {
  const shown: Set<ConfigPaneKey> = new Set<ConfigPaneKey>(
    options.panes
      .map((pane: IConfigSearchPane): ConfigPaneKey => pane.key)
      .filter((key: ConfigPaneKey): boolean => !options.hiddenPanes.includes(key))
  );

  return entries
    .filter((entry: IConfigSearchEntry): boolean => shown.has(entry.pane))
    .map((entry: IConfigSearchEntry): IConfigSearchHaystack => {
      const tooltip: string | undefined = entry.tooltipKey ? options.tooltipText(entry.tooltipKey) : undefined;
      return {
        entry,
        section: normalizeWithIndexMap(entry.sectionTitle),
        label: normalizeWithIndexMap(entry.label),
        description: entry.description ? normalizeWithIndexMap(entry.description) : undefined,
        tooltip: tooltip ? normalizeWithIndexMap(tooltip) : undefined,
        terms: entry.terms.map(normalizeWithIndexMap)
      };
    });
};

interface ITokenHit {
  score: number;
  labelRanges: IConfigSearchRange[];
  sectionRanges: IConfigSearchRange[];
  snippet?: IConfigSearchSnippet;
  /**
   * True when a dialog's field names are the BEST explanation of this token, not merely one
   * of them. "session" matching the Sessions category is not a reason to open its dialog,
   * even though a field in there happens to say "session" too.
   */
  wonOnTerms: boolean;
}

const allOccurrences = (text: INormalizedText, needle: string): IConfigSearchRange[] => {
  const ranges: IConfigSearchRange[] = [];
  let from: number = 0;
  while (from <= text.normalized.length - needle.length) {
    const at: number = text.normalized.indexOf(needle, from);
    if (at === -1) break;
    ranges.push(toOriginalRange(text, at, at + needle.length));
    from = at + needle.length;
  }
  return ranges;
};

const gradedScore = (
  text: INormalizedText,
  needle: string,
  exact: number,
  prefix: number,
  contains: number
): number => {
  if (text.normalized === needle) return exact;
  if (text.normalized.startsWith(needle)) return prefix;
  if (text.normalized.includes(needle)) return contains;
  return 0;
};

const snippetFrom = (
  text: INormalizedText,
  needle: string,
  source: ConfigSearchSnippetSource
): IConfigSearchSnippet => ({
  text: text.original,
  ranges: allOccurrences(text, needle),
  source
});

/**
 * One token against one entry: the best place it landed, with the ranges to mark there.
 * Every place it landed is marked, but only the best one is scored — a token that appears
 * in both the label and the description is not worth two hits.
 */
const matchToken = (haystack: IConfigSearchHaystack, needle: string): ITokenHit | undefined => {
  const hit: ITokenHit = { score: 0, labelRanges: [], sectionRanges: [], wonOnTerms: false };

  const sectionScore: number = gradedScore(
    haystack.section,
    needle,
    SCORE.sectionExact,
    SCORE.sectionPrefix,
    SCORE.sectionContains
  );
  if (sectionScore > 0) {
    hit.score = sectionScore;
    hit.sectionRanges = allOccurrences(haystack.section, needle);
  }

  if (haystack.entry.kind === "field") {
    const labelScore: number = gradedScore(
      haystack.label,
      needle,
      SCORE.labelExact,
      SCORE.labelPrefix,
      SCORE.labelContains
    );
    if (labelScore > 0) {
      hit.score = Math.max(hit.score, labelScore);
      hit.labelRanges = allOccurrences(haystack.label, needle);
    }
  } else {
    // A section's label IS its title. Scoring it twice would just inflate the number, and
    // the row draws the title as its label, so the same ranges serve both.
    hit.labelRanges = hit.sectionRanges;
  }

  const term: INormalizedText | undefined = haystack.terms.find((candidate: INormalizedText): boolean =>
    candidate.normalized.includes(needle)
  );
  if (term && SCORE.terms > hit.score) {
    hit.score = SCORE.terms;
    hit.snippet = snippetFrom(term, needle, "terms");
    hit.wonOnTerms = haystack.entry.termsOpenDialog;
  }

  // Prose never wins against a name, so it only ever supplies the line under the row — the
  // reader still needs to see WHY a row they did not name came back.
  if (!hit.snippet) {
    const prose: [INormalizedText | undefined, ConfigSearchSnippetSource][] = [
      [haystack.description, "description"],
      [haystack.tooltip, "tooltip"]
    ];
    for (const [text, source] of prose) {
      if (!text || !text.normalized.includes(needle)) continue;
      hit.score = Math.max(hit.score, SCORE.prose);
      hit.snippet = snippetFrom(text, needle, source);
      break;
    }
  }

  return hit.score > 0 ? hit : undefined;
};

/** Split the query on whitespace; every token must land somewhere for the entry to count. */
export const tokenizeQuery = (query: string): string[] =>
  normalizeConfigSearchText(query)
    .split(/\s+/)
    .filter((token: string): boolean => token.length > 0);

/**
 * The typo pass. Only for a single word, and only when nothing contained it: fuzzy on a
 * multi-word query matches almost everything, and run alongside real matches it reorders
 * them for no reason. `fzf` normalizes diacritics itself, so it is handed the text as
 * written and its positions index straight into it.
 *
 * Case-insensitive explicitly. fzf defaults to smart-case, which switches to case-SENSITIVE
 * the moment the query has a capital in it — so with Caps Lock on, or on a Turkish-caps
 * keyboard, "SESION" would find nothing while "sesion" found the setting.
 *
 * Its own ordering is kept, by scoring the results down the list. Stamping every hit with
 * one score let the sort below fall through to mount order, which put the best match
 * anywhere at all and, past the limit, dropped it — the run that made this obvious answered
 * "conection" with "App Subscription Service Account Json File" ahead of "Connection String".
 */
const fuzzyMatch = (haystacks: IConfigSearchHaystack[], query: string): IConfigSearchResult[] => {
  const selector = (haystack: IConfigSearchHaystack): string =>
    `${haystack.entry.label} ${haystack.entry.sectionTitle}`;
  const fzf: Fzf<IConfigSearchHaystack[]> = new Fzf<IConfigSearchHaystack[]>(haystacks, {
    selector,
    casing: "case-insensitive"
  });

  const hits: FzfResultItem<IConfigSearchHaystack>[] = fzf.find(query);
  return hits.map((hit: FzfResultItem<IConfigSearchHaystack>, rank: number): IConfigSearchResult => {
    const labelLength: number = hit.item.entry.label.length;
    const positions: number[] = [...hit.positions];
    return {
      entry: hit.item.entry,
      // Descending, so fzf's own order survives the sort below. The branch is exclusive — it
      // runs only when nothing was contained — so these never sit beside a substring score.
      score: SCORE.fuzzy + hits.length - rank,
      labelRanges: positionsToRanges(positions.filter((position: number): boolean => position < labelLength)),
      // The selector is "label sectionTitle", so a position past the label and its space
      // belongs to the title, offset by both.
      sectionRanges: positionsToRanges(
        positions
          .filter((position: number): boolean => position > labelLength)
          .map((position: number): number => position - labelLength - 1)
      ),
      openDialog: false
    };
  });
};

/**
 * Rank a built haystack against a query.
 *
 * Substring first, in every text, with every token required; fuzzy only when that finds
 * nothing at all, so a typo still gets an answer but a real match is never shuffled by one.
 * The order among equal scores is the order the settings mounted in — the reader's own order
 * through the editor — rather than anything alphabetical.
 */
export const searchConfigEntries = (
  haystacks: IConfigSearchHaystack[],
  query: string,
  options: IConfigSearchOptions
): IConfigSearchResult[] => {
  const tokens: string[] = tokenizeQuery(query);
  if (tokens.length === 0) return [];

  const results: IConfigSearchResult[] = [];
  haystacks.forEach((haystack: IConfigSearchHaystack): void => {
    let score: number = 0;
    let labelRanges: IConfigSearchRange[] = [];
    let sectionRanges: IConfigSearchRange[] = [];
    let snippet: IConfigSearchSnippet | undefined;
    let openDialog: boolean = false;

    for (const token of tokens) {
      const hit: ITokenHit | undefined = matchToken(haystack, token);
      if (!hit) return;
      score += hit.score;
      labelRanges = labelRanges.concat(hit.labelRanges);
      sectionRanges = sectionRanges.concat(hit.sectionRanges);
      if (hit.wonOnTerms) openDialog = true;
      if (!hit.snippet) continue;
      // The first source to answer sets the snippet; a later token only adds marks when it
      // landed in the SAME text. Keyed on the source alone, two tokens hitting two different
      // dialog field names put the second one's ranges on the first one's string — marking
      // characters that never matched, at offsets that can run off the end of it.
      if (!snippet) snippet = hit.snippet;
      else if (snippet.text === hit.snippet.text) {
        snippet = { ...snippet, ranges: snippet.ranges.concat(hit.snippet.ranges) };
      }
    }

    results.push({
      entry: haystack.entry,
      score,
      labelRanges: mergeRanges(labelRanges),
      sectionRanges: mergeRanges(sectionRanges),
      snippet: snippet ? { ...snippet, ranges: mergeRanges(snippet.ranges) } : undefined,
      openDialog
    });
  });

  const trimmed: string = query.trim();
  const ranked: IConfigSearchResult[] =
    results.length > 0 || tokens.length > 1 ? results : fuzzyMatch(haystacks, trimmed);

  const paneOrder: Map<ConfigPaneKey, number> = new Map<ConfigPaneKey, number>(
    options.panes.map((pane: IConfigSearchPane, index: number): [ConfigPaneKey, number] => [pane.key, index])
  );
  const mountOrder: Map<string, number> = new Map<string, number>(
    haystacks.map((haystack: IConfigSearchHaystack, index: number): [string, number] => [haystack.entry.id, index])
  );
  ranked.sort((a: IConfigSearchResult, b: IConfigSearchResult): number => {
    if (b.score !== a.score) return b.score - a.score;
    const paneDelta: number = (paneOrder.get(a.entry.pane) ?? 0) - (paneOrder.get(b.entry.pane) ?? 0);
    if (paneDelta !== 0) return paneDelta;
    return (mountOrder.get(a.entry.id) ?? 0) - (mountOrder.get(b.entry.id) ?? 0);
  });

  return ranked.slice(0, options.limit ?? DEFAULT_LIMIT);
};
